// navigation structure — mirrors the desktop mega-menu groups, each item declares the
// permission needed to see it; the shell hides items the user can't access. (route
// protection is also enforced server-side on every API call, this is UX only)
//
// items may nest one level via `children` to form submenus in the dropdown

import type { Permission } from '@technohull/shared';

export interface NavItem {
  label: string;
  /** leaf destination. omit when the item is a submenu parent (has `children`). */
  path?: string;
  /** external URL — opens in a new tab instead of navigating in-app (e.g. Gmail, which can't be framed). */
  external?: string;
  /** permission required to see + open this item (undefined = any signed-in user). */
  perm?: Permission;
  /** visible if the user has ANY of these permissions (for hub/landing links covering several pages). */
  anyPerm?: Permission[];
  /** not yet implemented — shown as a placeholder page. */
  soon?: boolean;
  /** work-in-progress — kept in the code + routed (reachable by URL) but hidden from the menu. */
  hidden?: boolean;
  /** submenu items (one level deep). */
  children?: NavItem[];
  /** the PC rail's glyph for a top-level item (see components/NavRail.tsx); submenu children carry none. */
  icon?: NavIcon;
}

/** the rail's icon set, drawn in components/NavRail.tsx. */
export type NavIcon =
  | 'explore' | 'dashboard' | 'agenda' | 'calendar' | 'hub' | 'delivery' | 'production' | 'people'
  | 'records' | 'progress' | 'tools' | 'checklist' | 'tag' | 'layers' | 'wrench' | 'inbox' | 'document'
  | 'trend' | 'contacts' | 'sheet' | 'history' | 'hash' | 'receipt' | 'box' | 'barcode' | 'orders';

/** permissions that grant access to some part of administration (for the admin hub link).
 * note: `configurator:manage` is intentionally NOT here — the configurator setup page lives under
 * Sales now, and sales users holding it must not gain an Admin Home link. */
export const ADMIN_PERMS: Permission[] = [
  'admin:users',
  'admin:roles',
  'admin:audit',
  'admin:config',
  'admin:mappings',
  'admin:tickets',
];

export interface NavGroup {
  title: string;
  items: NavItem[];
  /** gate the whole group on a single permission (in addition to each item's own perm). */
  perm?: Permission;
}

export const NAV: NavGroup[] = [
  {
    title: 'Main',
    items: [
      { label: 'Explore', path: '/', icon: 'explore' },
      { label: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
      { label: 'Agenda', path: '/agenda', perm: 'agenda:read', icon: 'agenda' },
      { label: 'Calendar', path: '/calendar', icon: 'calendar' },
    ],
  },
  {
    title: 'Management',
    perm: 'management:view',
    items: [
      { label: 'Management Hub', path: '/management-hub', icon: 'hub' },
      {
        label: 'Planning & Delivery',
        icon: 'delivery',
        children: [
          { label: 'Schedule', path: '/schedule', perm: 'schedule:read' },
          { label: 'Overview', path: '/overview', perm: 'overview:read' },
          { label: 'Pre-Delivery Alerts', path: '/assembly-alerts', perm: 'overview:read' },
          { label: 'Engine Orders', path: '/engine-orders', perm: 'overview:read' },
          { label: 'Washing Plan', path: '/washing', anyPerm: ['overview:read', 'service:read'] },
        ],
      },
      {
        label: 'Production',
        icon: 'production',
        children: [
          { label: 'Assembly Board', path: '/board', perm: 'board:read' },
          { label: 'Assembly Reporting', path: '/reporting', perm: 'report:read' },
          { label: 'Deck Flooring', path: '/deck', perm: 'overview:read' },
        ],
      },
      {
        label: 'People',
        icon: 'people',
        children: [
          { label: 'Employees', path: '/employees', perm: 'production:read' },
          { label: 'Overtime', path: '/overtime', perm: 'production:read' },
        ],
      },
      {
        label: 'Records',
        icon: 'records',
        children: [
          { label: 'Order Sheet', path: '/order-sheet', perm: 'production:read' },
          { label: 'Info / Files', path: '/info-files', perm: 'production:read' },
          { label: 'BOM lists', path: '/bom-lists', perm: 'production:read' },
        ],
      },
    ],
  },
  {
    title: 'Assembly',
    items: [
      { label: 'Assembly Hub', path: '/assembly-hub', icon: 'hub' },
      { label: 'Assembly Progress', path: '/assembly', perm: 'production:read', icon: 'progress' },
      {
        label: 'Tools & Internal Checks',
        icon: 'tools',
        children: [
          { label: 'Assembly Weights Editor', path: '/assembly-weights', perm: 'production:read' },
        ],
      },
      {
        label: 'Checklists',
        icon: 'checklist',
        children: [
          { label: 'DS - Programming', path: '/ds', perm: 'ds:read' },
          { label: 'PDI', path: '/pdi', perm: 'pdi:read' },
          { label: 'Loading', path: '/loading', perm: 'overview:read' },
        ],
      },
      {
        label: 'Miscellaneous',
        icon: 'tag',
        children: [
          { label: 'Labels', path: '/labels', perm: 'labels:read' },
        ],
      },
    ],
  },
  {
    title: 'GRP',
    perm: 'grp:read',
    items: [
      { label: 'GRP Inspection', path: '/grp', perm: 'grp:read', icon: 'layers' },
    ],
  },
  {
    title: 'Service',
    items: [
      { label: 'Service', path: '/service', perm: 'service:read', icon: 'wrench' },
      { label: 'Service Requests', path: '/service-requests', perm: 'servicereq:read', icon: 'inbox' },
      { label: 'Documents', path: '/documents', perm: 'service:read', icon: 'document' },
    ],
  },
  {
    title: 'Sales',
    items: [
      { label: 'Vessel Progress', path: '/vessel-progress', anyPerm: ['overview:read', 'sales:read'], icon: 'trend' },
      {
        label: 'Customer Database',
        icon: 'contacts',
        children: [
          { label: 'Customers', path: '/sales/customers', perm: 'customer_database.view' },
          { label: 'Duplicate Customers', path: '/sales/customers/duplicates', perm: 'customer_database.view' },
        ],
      },
      { label: 'Order Sheets', path: '/sales/sheets', perm: 'sales:read', icon: 'sheet' },
      { label: 'Change Log', path: '/sales/log', perm: 'sales:read', icon: 'history' },
    ],
  },
  {
    // the configurator and what follows an order: Sales builds and confirms it, Assembly accepts it and builds
    // from it. Sales opens the Sales submenu; Assembly both (rbac.ts DEPARTMENT_STANDARD).
    title: 'Configuration',
    items: [
      {
        label: 'Sales',
        icon: 'orders',
        children: [
          { label: 'Configurator', path: '/configurator', perm: 'order:create' },
          // (sales:read opens it by URL; the menu shows it to whoever builds or accepts orders, not every role that reads Sales)
          { label: 'Sales Orders', path: '/sales/orders', anyPerm: ['order:create', 'prodorders:view'] },
          { label: 'Configuration Setup', path: '/sales/configurator', perm: 'configurator:manage' },
        ],
      },
      {
        label: 'Assembly',
        icon: 'production',
        children: [
          { label: 'Assembly Orders', path: '/assembly-orders', perm: 'prodorders:view' },
        ],
      },
    ],
  },
  {
    title: 'Identification',
    items: [{ label: 'Vessel Identification', path: '/vessel-identification', perm: 'hin:read', icon: 'hash' }],
  },
  {
    title: 'Accounting',
    items: [{ label: 'Fueling Invoices', path: '/accounting/fueling', perm: 'fueling.view', icon: 'receipt' }],
  },
  {
    title: 'Warehouse',
    items: [
      { label: 'Vessel Status', path: '/vessel-status', perm: 'vesselstatus:read', icon: 'box' },
      { label: 'Fuel Tank - S/N', path: '/fuel-tank-sn', perm: 'vesselstatus:read', icon: 'barcode' },
      // the Order Sheet's rollup with the Watch-it box; it reads the order sheet, so production:read as there
      { label: 'Order Sheet Rollup', path: '/order-rollup', perm: 'production:read', icon: 'sheet' },
    ],
  },
  // Administration is intentionally NOT in the menu — it's reached from the profile (Settings), see
  // apps/web/src/pages/Settings.tsx. The routes still live in App.tsx and the Admin hub links to them.
];

/** all leaf items flattened (submenu children included) — for label lookups etc. */
export function navLeaves(): NavItem[] {
  const out: NavItem[] = [];
  for (const g of NAV) {
    for (const i of g.items) {
      if (i.children) out.push(...i.children);
      else out.push(i);
    }
  }
  return out;
}
